import { useEffect, useRef } from "react";
import { useFormikContext } from "formik";

/**
 * Debounced autosave helper for Formik forms.
 * - Saves after a debounce when values change
 * - Also flushes once on unmount (e.g. when navigating away) so you don't lose the last edits
 */
const AutoSave = ({ onAutoSave, debounceMs = 1200, enabled = true }) => {
  const { values, isSubmitting, dirty } = useFormikContext();

  const firstRunRef = useRef(true);
  const timeoutRef = useRef(null);
  const latestValuesRef = useRef(values);
  const latestDirtyRef = useRef(dirty);

  // Keep refs in sync with latest form state
  useEffect(() => {
    latestValuesRef.current = values;
    latestDirtyRef.current = dirty;
  }, [values, dirty]);

  // Main debounced autosave
  useEffect(() => {
    if (!enabled || !onAutoSave) return undefined;
    if (isSubmitting) return undefined;

    // Skip initial mount
    if (firstRunRef.current) {
      firstRunRef.current = false;
      return undefined;
    }

    // If nothing has changed, don't schedule a save
    if (!dirty) {
      clearTimeout(timeoutRef.current);
      return undefined;
    }

    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      onAutoSave(latestValuesRef.current);
    }, debounceMs);

    return () => clearTimeout(timeoutRef.current);
  }, [values, enabled, onAutoSave, isSubmitting, dirty]);

  // On unmount, flush latest values if there are unsaved changes
  useEffect(() => {
    return () => {
      if (!enabled || !onAutoSave) return;
      if (!latestDirtyRef.current) return;

      // Immediate, non-debounced save on unmount (e.g. route/step change)
      onAutoSave(latestValuesRef.current);
    };
  }, [enabled, onAutoSave]);

  return null;
};

export default AutoSave;

